import { useCallback, useState } from "react"
import {
  Box,
  Stepper,
  Typography,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  useTheme,
  Fab,
  Slide,
} from "@mui/material"

import ArrowBackIcon from "@mui/icons-material/ArrowBack"

import { useNavigate } from "react-router-dom"
import { useTranslation } from "providers/i18n"
import _ from "lodash"
import { Trans } from "react-i18next"

import { SwipeableDrawer } from "components/SwipeableDrawer"
import { useVenue } from "providers/venue"
import { OrdinalSelector } from "components/OrdinalSelector"
import { DirectionSearchContainer } from "containers/DirectionSearchContainer"
import { OriginMarkerIcon } from "components/icons"
import {
  DirectionSummary,
  DirectionStepItem,
  DirectionCardActions,
} from "components/domains/direction"
import { DestinationMarkerIcon } from "../../../../../components/icons"
import { useDirectionOverview } from "hooks/direction/useDirectionOverview"
import { useAppUI } from "providers/ui"

export const DirectionOverviewPage = () => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const { isMultiOrdinalVenue } = useVenue()

  const {
    steps,
    duration,
    distance,
    currentOrdinal,
    originData,
    originName,
    originLevelName,
    destinationData,
    destinationName,
    destinationLevelName,
    elevatorPreferredMode,
    relatedLevelWithSteps,
    scannedLocations,
    userLocation,
    changeOrdinal,
    toggleElevatorPreference,
    startNavigate,
  } = useDirectionOverview()

  const { t } = useTranslation()

  const { goBack } = useAppUI()

  const theme = useTheme()
  const { drawer: drawerProps } =
    theme.components[`AppDirectionOverview`]?.defaultProps || {}

  const { headerWrapper, paper, formControlLabel } =
    theme.components[`AppDirectionSearch`]?.styleOverrides || {}
  const {
    bleedWrapper,
    drawer,
    root,
    container,
    wrapper,
    stepper,
    stepOverviewContainer,
    numberStepsText,
  } = theme.components[`AppDirectionOverview`]?.styleOverrides || {}

  const { originIcon, destinationIcon, highlightedText } =
    theme.components[`DirectionStep`]?.styleOverrides || {}

  const originFloorText = isMultiOrdinalVenue
    ? `(${t("At Floor", {
        value: originLevelName,
      })})`
    : ""
  const destinationFloorText = isMultiOrdinalVenue
    ? `(${t("At Floor", {
        value: destinationLevelName,
      })})`
    : ""
  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const handleOpenDrawer = useCallback(() => {
    setOpen(true)
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setOpen(false)
  }, [])

  const handleChange = useCallback(
    ({ origin, destination }) => {
      const originParam = origin?.id || "unknown"
      const destinationParam = destination?.id || "unknown"
      navigate(`/maps/dir/${originParam}/${destinationParam}`, {
        replace: true,
      })
    },
    [navigate]
  )

  const handleClickStep = useCallback(() => setOpen((prev) => !prev), [])
  const stepsAvailable = steps && steps.length > 0
  return (
    <>
      <Box sx={root}>
        <Box sx={headerWrapper}>
          <Stack spacing={2} alignItems="flex-start">
            <DirectionSearchContainer
              origin={originData}
              destination={destinationData}
              onChange={handleChange}
              onBack={handleClickBack}
              userLocation={userLocation}
              lastScannedLocation={_.last(scannedLocations)}
            />
            <Paper elevation={1} sx={paper}>
              <FormControlLabel
                control={
                  <Switch
                    checked={elevatorPreferredMode}
                    onChange={toggleElevatorPreference}
                  />
                }
                label={t("Avoid Steps")}
                componentsProps={{ typography: { variant: "body2" } }}
                sx={formControlLabel}
              />
            </Paper>
          </Stack>
        </Box>

        {/* //TODO:: find a way for adding active ordinal (selectedOrdinal) */}
        {isMultiOrdinalVenue && (
          <OrdinalSelector
            levels={relatedLevelWithSteps}
            onClickOrdinal={changeOrdinal}
            selectedOrdinal={currentOrdinal}
          />
        )}
      </Box>

      {stepsAvailable && (
        <>
          {/* Direction Summary (kiosk) */}
          <Slide
            direction="up"
            in={stepsAvailable && !open}
            mountOnEnter
            unmountOnExit
          >
            <Box sx={container}>
              <Fab variant="rounded" onClick={handleClickBack}>
                <ArrowBackIcon />
              </Fab>
              <Paper sx={{ width: "100%" }}>
                <Box sx={wrapper}>
                  <DirectionSummary
                    primary={t("Travel", {
                      duration,
                      distance,
                    })}
                    secondary={
                      <Trans
                        components={{
                          destination: highlightedStepText,
                        }}
                      >
                        {t("Origin To Destination", {
                          origin: `${t(originName, {
                            ns: "imdf",
                          })} ${originFloorText}`,
                          destination: `${t(destinationName, {
                            ns: "imdf",
                          })} ${destinationFloorText}`,
                        })}
                      </Trans>
                    }
                  />
                  <DirectionCardActions
                    onStart={startNavigate}
                    onStep={handleClickStep}
                  />
                </Box>
              </Paper>
            </Box>
          </Slide>
          {/* Direction Summary (mobile) */}
          <SwipeableDrawer
            {...drawerProps}
            open={open}
            onClose={handleCloseDrawer}
            onOpen={handleOpenDrawer}
            contentProps={{ onTouchStart: () => {} }}
            sx={drawer}
          >
            <Box sx={bleedWrapper}>
              <DirectionSummary
                primary={t("Travel", {
                  duration,
                  distance,
                })}
                secondary={
                  <Trans
                    components={{
                      destination: highlightedStepText,
                    }}
                  >
                    {t("Origin To Destination", {
                      origin: `${t(originName, { ns: "imdf" })}`,
                      destination: `${t(
                        t(destinationName, { ns: "imdf" })
                      )} ${destinationFloorText}`,
                    })}
                  </Trans>
                }
              />
              <DirectionCardActions
                onStart={startNavigate}
                onStep={handleClickStep}
              />
            </Box>

            <Box sx={stepOverviewContainer}>
              <Typography sx={numberStepsText} variant="body2">
                {t("NumberSteps", {
                  count: steps?.length || 0,
                })}
              </Typography>
              <Stepper sx={stepper} orientation="vertical" connector={null}>
                {originData && (
                  <DirectionStepItem
                    primaryLabel={
                      <Trans
                        components={{
                          location: highlightedStepText,
                        }}
                      >
                        {t("Location Floor", {
                          location: t(originName, { ns: "imdf" }),
                          level: originData.level_name,
                        })}
                      </Trans>
                    }
                    secondaryLabel={t("Your last scanned location")}
                    stepLabelProps={{
                      icon: <OriginMarkerIcon sx={originIcon} />,
                    }}
                  />
                )}
                {steps.map((step, index) => {
                  const { description } = step
                  return (
                    <DirectionStepItem
                      index={index}
                      navigateTo={`navigate/steps/${index + 1}`}
                      primaryLabel={
                        <Trans
                          components={{
                            category: highlightedStepText,
                            destination: highlightedStepText,
                            landmark: highlightedStepText,
                          }}
                        >
                          {t(description.text, {
                            category: t(description.options?.category, {
                              ns: "imdf",
                            }),
                            destination: t(description.options?.destination, {
                              ns: "imdf",
                            }),
                            landmark: t(description.options?.landmark),
                          })}
                        </Trans>
                      }
                      key={`step-description-${index}`}
                    />
                  )
                })}
                {destinationData && (
                  <DirectionStepItem
                    primaryLabel={
                      <Trans
                        components={{
                          location: highlightedStepText,
                        }}
                      >
                        {t("Location Floor", {
                          location: t(destinationName, { ns: "imdf" }),
                          level: destinationData.level_name,
                        })}
                      </Trans>
                    }
                    secondaryLabel={t("Your Destination")}
                    stepLabelProps={{
                      icon: <DestinationMarkerIcon sx={destinationIcon} />,
                    }}
                  />
                )}
              </Stepper>
            </Box>
          </SwipeableDrawer>
        </>
      )}
    </>
  )
}
